import React, { useEffect, useState } from "react";
import { useSpring, animated } from "@react-spring/web";
import styled from "styled-components";
import cx from "classnames";
import { useMeasure } from "react-use";
import { CheckMark } from "../checkMark";
import { withCentered } from "../centerer";
import { ButtonBox, StyledButton } from "../button/button.styles";
import { UnStyledButton } from "../textButton";
import { useSelector } from "react-redux";

const CenteredCheckMark = withCentered(CheckMark);

const runUpdate = (instantTrue, delay, update) => {
  let timeoutId = undefined;
  if (instantTrue) {
    update();
  } else {
    timeoutId = setTimeout(() => {
      update();
    }, delay);
  }
  
  return timeoutId;
};

const useDelayedBoolean = (liveValue, {delay = 2000, instantTrue, instantFalse} = {}) => {
  const [delayedIsFetching, setDelayedIsFetching] = useState(liveValue);
  
  useEffect(() => {
    let timeoutId = undefined;
    if (liveValue) {
      timeoutId = runUpdate(instantTrue, delay, () => setDelayedIsFetching(liveValue));
    } else {
      timeoutId = runUpdate(instantFalse, delay, () => setDelayedIsFetching(liveValue));
    }

    return () => clearTimeout(timeoutId);
  }, [liveValue, delayedIsFetching, setDelayedIsFetching, delay, instantTrue, instantFalse]);
  
  return delayedIsFetching;
};

const calculateStrokeWidth = (height) => {
  const cutoffButtonHeight = 100;
  const minButtonHeight = 40;
  const maxStrokeWidth = 9;
  const minStrokeWidth = 4;
  const percentWayToCutoff = (height - minButtonHeight) / (cutoffButtonHeight - minButtonHeight);
  const clampedPercentWay = Math.max(Math.min(percentWayToCutoff, 1), 0);

  const strokeWidth = minStrokeWidth + ((maxStrokeWidth - minStrokeWidth) * (1 - clampedPercentWay))
  return strokeWidth;
};

const AnimatedButtonBox = styled(ButtonBox).attrs({as: animated.div})``;

const getBorderRadius = (theme) =>  {
  return theme === 'nimbleTheme' ? 4 : 25;
}

export const NextButton = ({ onClick, text = "Next", isFetching, children, dynamicBallHeight, form, type, className, disabled, id, name }) => {
  const themeName = useSelector(state => state.StartUp.theme.themeName);
  const [outerRef, {width, height}] = useMeasure();

  const singleLineButtonHeight = 40;
  const ballDiameter = dynamicBallHeight ? height : singleLineButtonHeight;
  
  const delayedIsFetching = useDelayedBoolean(isFetching, { instantTrue: true });

  const textSpring = useSpring({
    opacity: delayedIsFetching ? 0 : 1,
    transform: `translate3d(0, ${delayedIsFetching ? -10 : 0}px, 0)`,
    from: {
      opacity: 1,
      transform: "translate3d(0, 0px, 0)",
    },
  });
  
  const sizeSpring = useSpring({
    width: delayedIsFetching ? ballDiameter : (width > 0 ? width : "auto"),
    height: delayedIsFetching ? ballDiameter : (height > 0 ? height : "auto"),
    borderRadius: `${getBorderRadius(themeName)}px`,
    overflow: delayedIsFetching ? "hidden" : "visible",
    
  });

  const delayedCheckMarkVisible = useDelayedBoolean(delayedIsFetching, { instantFalse: true, delay: 500 });

  const buttonBoxSpring = useSpring({
    opacity: delayedCheckMarkVisible ? 0 : 1,
    from: {
      opacity: 1
    }
  });
  
  const checkMarkSpring = useSpring({
    opacity: delayedCheckMarkVisible ? 1 : 0,
  });
  
  const [hasFocus, setHasFocus] = useState(false);

  const disabledButton = delayedIsFetching || disabled;
  
  return (
    <UnStyledButton 
      onClick={onClick}
      className={className}
      form={form}
      type={type}
      onFocus={() => setHasFocus(true)} 
      onBlur={() => setHasFocus(false)}
      disabled={disabledButton} 
      removeOutline ref={outerRef}
      aria-label={text || "Submit"}
      style={{maxWidth: "371px", width: "100%", position: "relative", display: "flex", justifyContent: "center"}}
      id={id}
      name={name}
    >
      <animated.div style={{...sizeSpring, position: "absolute", top: "50%", left: "50%", right: 0, bottom: 0, display: "flex", transform: "translate3d(-50%,-50%,0)"}}>
        <AnimatedButtonBox 
          style={{...buttonBoxSpring, flex: 1, alignSelf: "stretch", borderRadius: `${getBorderRadius(themeName)}px`}}
          className={cx({"hover-on": hasFocus, "no-shadows": delayedCheckMarkVisible || delayedIsFetching})}
        />
      </animated.div>
      <StyledButton
        theme={themeName}
        className="no-shadows"
        disabled={disabledButton}
        $elementType="div"
      >
      <animated.div style={textSpring} className="button-text-color">
        {children || text}
      </animated.div>
      </StyledButton>
      {delayedIsFetching && (
        <animated.div style={checkMarkSpring}>
          <CenteredCheckMark strokeWidth={calculateStrokeWidth(ballDiameter)} height={ballDiameter} checked={!isFetching} />
        </animated.div>
      )}
    </UnStyledButton>
  );
}